/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';

import { createSlice } from '@reduxjs/toolkit';

import type { PriceData } from '../../../models/priceData/priceData';
import type { CartSummaryState } from '../../structureDefinitions/cartState';

import persistance from '../../../persistance/persistance';
import { DeliveryMode } from '../../structureDefinitions/storeState';
import { getStoreContextFromState } from '../store/storeActions.utils';
import { setFullCart } from './cartHydrationActions';

export const getInitialState = (): CartSummaryState => {
    const storeContext = persistance.storeContext.get();
    const validState = getStoreContextFromState(storeContext);
    return {
        /** postCode is used in restoring cart if it doesnt exist, so createcart will use it to set postalcode, otherwise its never used for storecontext */
        postCode:
            validState.deliveryMode === DeliveryMode.homedelivery ? validState.zipCode : undefined,
        appliedVouchers: [],
        quantity: 0,
        cartId: '',
        delivery: {
            // This is either pickup or home delivery
            mode: DeliveryMode.homedelivery,
            // The code is used to identify all of the different modes, pickup has several codes depending on the type of pickup, pickup from lockers, pickup from staff, etc.
            code: DeliveryMode.homedelivery,
            // The name is used for display purposes
            name: 'Hemleverans',
        },
        extraAmountToBeReserved: 0,
        leftToCheckout: 0,
        tax: 0,
        totalTaxValues: [],
        appliedOrderPromotions: [],
        deliveryFeePriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        totalPriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        productDiscountPriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        totalProductPriceWithDiscounts: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        totalDepositSum: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        productSumPriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        productSumWithoutDiscountPriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        totalDiscountPriceData: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        totalPriceExclReservationFee: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
        minimumShoppingValue: {
            exclTaxPrice: undefined,
            inclTaxPrice: undefined,
        },
    };
};

const slice = createSlice({
    name: 'summary',
    initialState: getInitialState(),
    reducers: {
        updateQuantity: (state, action: PayloadAction<number>) => {
            state.quantity = action.payload;
            return state;
        },
        updateTotals: (
            state,
            action: PayloadAction<{
                totalPrice: PriceData;
                totalProductPriceWithDiscounts: PriceData;
            }>,
        ) => {
            state.totalPriceData = action.payload.totalPrice;
            state.totalProductPriceWithDiscounts = action.payload.totalProductPriceWithDiscounts;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setFullCart, (_state, action) => action.payload.summary);
    },
});

export const cartSummaryActions = { ...slice.actions };
export default slice.reducer;
